import { getHeaders } from "../../api/api";
import { DocumentationApiError, DocumentationDefinitionCreateInput, DocumentationDefinitionVersion } from "./models";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

async function requestHeaders(token?: string, idempotencyKey?: string): Promise<Record<string, string>> {
    return {
        ...(await getHeaders(token)),
        "Content-Type": "application/json",
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {})
    };
}

async function checked(response: Response): Promise<Response> {
    if (response.ok) return response;
    let code = "DOCUMENTATION_REQUEST_FAILED";
    let message = `Documentation request failed (${response.status}).`;
    let details: Record<string, unknown> | undefined;
    try {
        const payload = await response.json();
        code = payload?.error?.code || code;
        message = payload?.error?.message || message;
        details = payload?.error?.details;
    } catch {
        // Keep the status-based error when the response has no JSON body.
    }
    throw new DocumentationApiError(code, message, response.status, details);
}

export async function listDocumentationDefinitions(
    scopeType: "Organization" | "Business Area",
    scopeId: string,
    token?: string
): Promise<DocumentationDefinitionVersion[]> {
    const query = new URLSearchParams({ scope_type: scopeType, scope_id: scopeId, include_archived: "false" });
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions?${query}`, {
            headers: await getHeaders(token)
        })
    );
    return (await response.json()).definitions;
}

export async function createDocumentationDefinition(
    definition: DocumentationDefinitionCreateInput,
    idempotencyKey: string,
    token?: string
): Promise<DocumentationDefinitionVersion> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions`, {
            method: "POST",
            headers: await requestHeaders(token, idempotencyKey),
            body: JSON.stringify(definition)
        })
    );
    return response.json();
}

export async function updateDocumentationDefinition(definition: DocumentationDefinitionVersion, token?: string): Promise<DocumentationDefinitionVersion> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definition.id)}`, {
            method: "PUT",
            headers: await requestHeaders(token),
            body: JSON.stringify({ ...definition, expected_revision: definition.revision })
        })
    );
    return response.json();
}

export async function createDefinitionEditDraft(definitionId: string, token?: string): Promise<DocumentationDefinitionVersion> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definitionId)}/edit-draft`, {
            method: "POST",
            headers: await requestHeaders(token, crypto.randomUUID())
        })
    );
    return response.json();
}

export async function validateDocumentationDefinition(
    definitionId: string,
    token?: string
): Promise<{ valid: boolean; issues: Array<{ code: string; path: string; message: string }> }> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definitionId)}/validate`, {
            method: "POST",
            headers: await requestHeaders(token)
        })
    );
    return response.json();
}

export async function testDocumentationDefinition(
    definition: DocumentationDefinitionVersion,
    sampleInput: string,
    language: string,
    sampleContext: Record<string, unknown>,
    sampleFields: Record<string, unknown>,
    token?: string
): Promise<{ success: boolean; definition?: DocumentationDefinitionVersion }> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definition.id)}/test`, {
            method: "POST",
            headers: await requestHeaders(token),
            body: JSON.stringify({
                expected_revision: definition.revision,
                language,
                sample_input: sampleInput,
                sample_context: sampleContext,
                sample_fields: sampleFields
            })
        })
    );
    return response.json();
}

export async function publishDocumentationDefinition(definition: DocumentationDefinitionVersion, token?: string): Promise<DocumentationDefinitionVersion> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definition.id)}/publish`, {
            method: "POST",
            headers: await requestHeaders(token, `publish-${definition.id}-${definition.revision}`),
            body: JSON.stringify({ expected_revision: definition.revision })
        })
    );
    return response.json();
}

export async function archiveDocumentationDefinition(definition: DocumentationDefinitionVersion, token?: string): Promise<DocumentationDefinitionVersion> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definition.id)}/archive`, {
            method: "POST",
            headers: await requestHeaders(token, `archive-${definition.id}-${definition.revision}`),
            body: JSON.stringify({ expected_revision: definition.revision })
        })
    );
    return response.json();
}

export async function deleteDocumentationDefinition(definition: DocumentationDefinitionVersion, token?: string): Promise<void> {
    await checked(
        await fetch(`${BACKEND_URI}/documentation/definitions/${encodeURIComponent(definition.id)}`, {
            method: "DELETE",
            headers: await requestHeaders(token),
            body: JSON.stringify({ expected_revision: definition.revision })
        })
    );
}
